import FormatCurrency from 'components/Common/FormatCurrency'
import Caption from 'components/Luxkit/Typography/Caption'
import Heading from 'components/Luxkit/Typography/Heading'
import GeoContext from 'contexts/geoContext'
import React, { ComponentProps, useContext, useMemo } from 'react'

interface Props {
  size: 'S' | 'M' | 'L'
  caption?: string
  /**
   * Main price
   */
  price: number
  /**
   * This value is used to determine whether the inclusivity of taxes and fees
   * should be highlighted in the current region or not.
   *
   * @example 0 means inclusive
   */
  taxesAndFees: number | 'inclusive' | 'exclusive'
  /**
   * @example $999.99 /room
   * @example $999.99 total
   *
   * A leading "/" will be prepended for non-"total"
   */
  saleUnit: string
  'data-testid'?: string
  /**
   * @example +$999.99
   * @example -$999.99
   * @default false
   */
  showPriceSign?: boolean
}

const SIZES_HEADING_VARIANTS: Record<Props['size'], ComponentProps<typeof Heading>['variant']> = {
  S: 'heading6',
  M: 'heading5',
  L: 'heading4',
}

const SIZES_CAPTION_VARIANTS: Record<Props['size'], ComponentProps<typeof Caption>['variant']> = {
  S: 'medium',
  M: 'large',
  L: 'large',
}

/**
 * This component shall not be used directly.
 * Please use `<PriceRowPrice>` or `<PriceRowPriceWithCaption>` instead.
 */
function BasePriceRowPrice(props: Props) {
  const {
    size,
    caption,
    price,
    saleUnit,
    taxesAndFees,
    showPriceSign,
    'data-testid': TestId,
  } = props

  const geo = useContext(GeoContext)
  const shouldHighlightPrice = geo.isTaxExclusiveRegion && taxesAndFees !== 'exclusive' && (taxesAndFees === 0 || taxesAndFees === 'inclusive')
  const sign = (showPriceSign && price > 0) ? '+' : undefined
  const saleUnitLabel = useMemo(() => {
    if (!saleUnit) return undefined
    const sanitisedSaleUnit = saleUnit.toLocaleLowerCase().replace(/^\//, '')
    if (sanitisedSaleUnit === 'total' || sanitisedSaleUnit === 'total*') return sanitisedSaleUnit
    return `/${sanitisedSaleUnit}`
  }, [saleUnit])

  return <Heading
    data-testid={TestId}
    variant={SIZES_HEADING_VARIANTS[size]}
    colour={shouldHighlightPrice ? 'highlight-secondary' : undefined}
    wrap="no-wrap"
  >
    {!!caption && <>
      <Caption
        as="span"
        variant={SIZES_CAPTION_VARIANTS[size]}
        colour="neutral-two"
        weight="normal"
        format="propercase"
      >
        {caption}
      </Caption>
      {' '}
    </>}
    {sign}
    <FormatCurrency value={price} format="roundedDollar" />
    {saleUnitLabel && <>
      {' '}
      <Caption
        as="span"
        variant={SIZES_CAPTION_VARIANTS[size]}
        colour="neutral-two"
        weight="normal"
        format="lowercase"
      >
        {saleUnitLabel}
      </Caption>
    </>}
  </Heading>
}

export default BasePriceRowPrice
